import { useState } from 'react';
import { useNavigate, useLocation, useSearchParams, Link } from 'react-router-dom';
import { useAuthStore } from '@stores/auth-store';
import { useExternalPointStore } from '@stores/external-point-store';
import * as ToastUtil from '@utils/toast-utils';
import { ROUTE_CONFIGS } from '@configs/routes';

export const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user, signOut: authSignOut } = useAuthStore();
  const activePoint = useExternalPointStore((s) => s.activePoint);
  const isProductsPage = location.pathname === '/products' || location.pathname.startsWith('/products/');
  const showRefMode = !!(activePoint && isProductsPage);

  const [headerSearchInput, setHeaderSearchInput] = useState('');

  const handleHeaderSearch = (keyword: string) => {
    const ref = searchParams.get('ref') || activePoint?.code || '';
    const params = new URLSearchParams();
    if (ref) params.set('ref', ref);
    if (keyword) params.set('keyword', keyword);
    navigate(`/products?${params.toString()}`);
  };

  const handleMenuOpen = () => {
    document.body.classList.add('hidden');
    document.querySelector('.header')?.classList.add('open');
    document.querySelector('.navbox')?.classList.add('open');
  };

  const handleSignOut = async () => {
    const { signOut } = await import('@features/user/service');
    const result = await signOut();
    if (result.status === 200) {
      authSignOut();
      navigate('/');
      ToastUtil.success('EyeTMarket에서 로그아웃하였습니다.');
    }
  };

  return (
    <header className="header">
      <div className="inner h_inner">
        <div className="h_logo" style={showRefMode ? { width: '8rem' } : undefined}>
          <Link
            to={
              showRefMode
                ? `/products?ref=${encodeURIComponent(searchParams.get('ref') || activePoint?.code || '')}`
                : '/'
            }
          >
            {showRefMode && activePoint?.headerImageUrl ? (
              <img src={activePoint.headerImageUrl} alt={activePoint.name} style={{ maxHeight: '2.5rem' }} />
            ) : (
              <img src="/publish/img/webp/logo.webp" alt="" />
            )}
          </Link>
        </div>
        {showRefMode ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 2rem' }}>
            <div
              style={{
                width: '100%',
                maxWidth: '500px',
                height: '2.25rem',
                border: '2px solid #4949e9',
                borderRadius: '0.438rem',
                background: '#fff',
                padding: '0 0.75rem 0 1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
              }}
            >
              <input
                type="text"
                placeholder="분석내용, 모델링명을 입력하세요"
                value={headerSearchInput}
                onChange={(e) => setHeaderSearchInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleHeaderSearch(headerSearchInput.trim()); }}
                style={{
                  flex: 1,
                  border: 'none',
                  background: 'none',
                  fontSize: '0.8125rem',
                  color: '#000',
                  outline: 'none',
                }}
              />
              <button
                type="button"
                onClick={() => handleHeaderSearch(headerSearchInput.trim())}
                style={{
                  background: 'url(/publish/img/webp/ico_search.webp) no-repeat center/contain',
                  width: '1.125rem',
                  aspectRatio: '1/1',
                  border: 'none',
                  cursor: 'pointer',
                  flexShrink: 0,
                }}
              />
            </div>
          </div>
        ) : (
          <ul className="h_nav">
            <li className={location.pathname === '/product-intro' ? 'active' : ''}>
              <Link to="/product-intro" className="link">
                제품 소개
              </Link>
            </li>
            <li
              className={
                location.pathname === '/products' ||
                (location.pathname.startsWith('/products/') && location.pathname !== '/product-intro')
                  ? 'active'
                  : ''
              }
            >
              <Link to="/products" className="link">
                분석 스토어
              </Link>
            </li>
            <li className={location.pathname === '/inquiry' ? 'active' : ''}>
              <Link to="/inquiry" className="link">
                문의하기
              </Link>
            </li>
            <li className={location.pathname === '/education-course' ? 'active' : ''}>
              <Link to="/education-course" className="link">
                교육과정
              </Link>
            </li>
            <li className={location.pathname === '/analysis-request' ? 'active' : ''}>
              <Link to="/analysis-request" className="link">
                분석 의뢰하기
              </Link>
            </li>
          </ul>
        )}
        <div className="h_side">
          {showRefMode ? (
            <Link
              to={ROUTE_CONFIGS.EYET_DOWNLOAD.path}
              className="side_btn pc download_btn"
              data-track="헤더_무료다운로드_버튼"
              data-track-page="global"
            >
              무료 다운로드
            </Link>
          ) : user ? (
            <>
              <Link to="/my-page" className="side_btn pc">
                마이페이지
              </Link>
              {user.role === 'ADMIN' && (
                <Link to="/admin" className="side_btn pc">
                  관리자 페이지
                </Link>
              )}
              <button type="button" className="side_btn pc" onClick={handleSignOut}>
                로그아웃
              </button>
              <Link
                to={ROUTE_CONFIGS.EYET_DOWNLOAD.path}
                className="side_btn pc download_btn"
                data-track="헤더_무료다운로드_버튼"
                data-track-page="global"
              >
                무료 다운로드
              </Link>
            </>
          ) : (
            <>
              <a
                href={`/sign-in?returnTo=${encodeURIComponent(location.pathname + location.search)}`}
                className="side_btn pc"
                data-track="헤더_로그인_버튼"
              >
                로그인
              </a>
              <Link to="/sign-up" className="side_btn pc" data-track="헤더_회원가입_버튼">
                회원가입
              </Link>
              <Link
                to={ROUTE_CONFIGS.EYET_DOWNLOAD.path}
                className="side_btn pc download_btn"
                data-track="헤더_무료다운로드_버튼"
                data-track-page="global"
              >
                무료 다운로드
              </Link>
            </>
          )}
          <button type="button" className="menu_open tablet" onClick={handleMenuOpen}></button>
        </div>
      </div>
    </header>
  );
};
